// Cari özeti: e-belge rozeti, GİB unvanı eşleşmesi, vade/risk ve kart durumu.
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RISK_ACTION_BLOCK, type AccountCardForm, type AccountProblem } from '../useAccountCardForm';
import { L } from './fields';

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>{value}</Typography>
  </Box>
);

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4, letterSpacing: '0.08em' }}>{children}</Typography>
);

export const SummaryPanel: React.FC<{ f: AccountCardForm; onGoTo: (section: AccountProblem['section']) => void }> = ({ f, onGoTo }) => {
  const { t } = useTranslation('accounting');
  const d = f.accountData;
  const r = f.lookup;

  const badge = r?.status === 'found'
    ? <Chip size="small" color="success" variant="outlined" label={t(`${L}.lookup.eInvoice`, 'e-Fatura mükellefi')} />
    : r?.status === 'not-einvoice'
      ? <Chip size="small" color="info" variant="outlined" label={t(`${L}.lookup.eArchive`, 'e-Arşiv')} />
      : <Chip size="small" variant="outlined" label={t(`${L}.layout.notQueriedShort`, 'Sorgulanmadı')} />;

  const symbol = f.getCurrencySymbol(d.riskLimitCurrency);
  const riskText = d.riskLimitAmount === ''
    ? t(`${L}.layout.riskUnlimited`, 'Sınırsız')
    : Number(d.riskLimitAmount) === 0
      ? t(`${L}.layout.riskNoCredit`, 'Veresiye yok')
      : `${Number(d.riskLimitAmount).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ${symbol}`;

  const titleMatch = r?.status === 'found' && r.title
    ? (r.title.trim().toLocaleUpperCase('tr-TR') === d.companyName.trim().toLocaleUpperCase('tr-TR')
      ? t(`${L}.layout.titleMatches`, 'Eşleşiyor')
      : t(`${L}.layout.titleDiffers`, 'Farklı'))
    : null;

  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      <Heading>{t(`${L}.layout.summary`, 'Cari özeti')}</Heading>
      <Box>{badge}</Box>
      {titleMatch && <Row label={t(`${L}.lookup.gibTitle`, 'GİB unvanı')} value={titleMatch} />}
      <Row label={t(`${L}.layout.dueShort`, 'Vade')} value={d.paymentDueDays ? `${d.paymentDueDays} ${t(`${L}.layout.days`, 'gün')}` : '—'} />
      <Row label={t(`${L}.layout.riskLimit`, 'Risk limiti')} value={riskText} />
      {d.riskLimitAmount !== '' && (
        <Row
          label={t(`${L}.layout.onExceedLong`, 'Limit aşılınca')}
          value={d.riskLimitAction === RISK_ACTION_BLOCK ? t(`${L}.layout.block`, 'Engelle') : t(`${L}.layout.warnOnly`, 'Uyar')}
        />
      )}
      {!f.isActive && <Box><Chip size="small" label={t(`${L}.layout.passive`, 'Pasif')} /></Box>}

      <Heading>{t(`${L}.layout.cardStatus`, 'Kart durumu')}</Heading>
      {f.problems.length === 0 ? (
        <Box><Chip size="small" color="success" variant="outlined" label={t(`${L}.layout.readyToSave`, 'Kaydetmeye hazır')} /></Box>
      ) : (
        f.problems.map(p => (
          <Box key={p.message}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={p.message}
              onClick={() => onGoTo(p.section)}
              sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
            />
          </Box>
        ))
      )}
    </Box>
  );
};
